import React, { type ReactNode } from 'react';
import styled, { css } from 'styled-components';
import { Button } from 'components/Button';
import {
    BoxShadowMedium,
    ColorNeutralWhite,
    ColorNordicGrey1,
    Spacing16,
    Spacing24,
    Spacing40,
    Spacing8
} from 'autogen/design-tokens/tokens';
import { breakpoint, sizes } from 'variables';
import QuickView, { useQuickView } from 'components/quickView';
import { Close } from 'autogen/translation-keys/trans-website-shared';
import UAEventTracking, { type TrackingCategory } from 'modules/tracking/UAEventTracking';
import { useTranslation } from 'react-i18next';
import { toHtmlContent } from 'modules/helpers/strings';
import { Typography } from 'components/generic/typography';

function getButtonLocationStyling(buttonLocation) {
    switch (buttonLocation) {
        case 'Center':
            return css`
                left: 50%;
                transform: translateX(-50%);
            `;
        case 'Right':
            return css`
                right: ${Spacing8};

                ${breakpoint.up(sizes.xl)} {
                    right: ${Spacing16};
                }
            `;
        default:
            return css`
                left: ${Spacing8};

                ${breakpoint.up(sizes.xl)} {
                    left: ${Spacing16};
                }
            `;
    }
}

const StyledButton = styled(Button)<{ buttonLocation: 'Left' | 'Center' | 'Right'; relative: boolean }>`
    box-shadow: ${BoxShadowMedium};
    border: 1px solid transparent;

    ${({ relative, buttonLocation }) =>
        !relative &&
        css`
            position: absolute;
            bottom: ${Spacing8};
            ${breakpoint.up(sizes.xl)} {
                bottom: ${Spacing16};
            }
            ${getButtonLocationStyling(buttonLocation)}
        `}

    &:hover {
        background: ${ColorNeutralWhite};
        border: 1px solid ${ColorNordicGrey1};
    }
`;

const ModalContent = styled.div`
    padding: ${Spacing8} ${Spacing40} ${Spacing40};
    display: flex;
    flex-direction: column;
    width: 100%;
    overflow-y: auto;
    max-width: 1000px;
`;

const CloseButton = styled(Button)`
    margin-top: ${Spacing24};
`;

interface IDisclaimerButtonProps {
    disclaimerText?: string | ReactNode;
    buttonText?: string;
    buttonLocation?: 'Left' | 'Center' | 'Right';
    quickViewId: string;
    trackingCategory?: TrackingCategory;
    relative?: boolean;
}

export function DisclaimerButton(props: IDisclaimerButtonProps): JSX.Element {
    const {
        disclaimerText,
        buttonText,
        buttonLocation = 'Right',
        quickViewId,
        trackingCategory,
        relative = false,
        ...rest
    } = props;

    const { t } = useTranslation();
    const { toggle } = useQuickView(quickViewId);

    function toggleQuickView(visible: boolean) {
        toggle(visible);

        if (trackingCategory) {
            UAEventTracking({
                eventCategory: trackingCategory,
                eventAction: visible ? 'openModal' : 'closeModal'
            });
        }
    }

    if (!disclaimerText) return <>{null}</>;

    return (
        <>
            <StyledButton
                buttonLocation={buttonLocation}
                variant="negative"
                size="sm"
                onClick={(event) => {
                    event.preventDefault(); // Stops navigation if nested in an anchor.
                    toggleQuickView(true);
                }}
                relative={relative}
                {...rest}
            >
                {buttonText}
            </StyledButton>
            <QuickView quickViewId={quickViewId}>
                <ModalContent>
                    <Typography.Body size="L">
                        {typeof disclaimerText === 'string'
                            ? toHtmlContent(disclaimerText.replaceAll('\n', '<br/>'))
                            : disclaimerText}
                    </Typography.Body>
                    <CloseButton variant="primary" onClick={() => toggleQuickView(false)}>
                        {t(Close)}
                    </CloseButton>
                </ModalContent>
            </QuickView>
        </>
    );
}

export default DisclaimerButton;
